import type { Mutable } from 'utility-types'

import { assert, executeOnce, isArrayNotEmpty, type ReadonlyDate, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../../utils'

import { Capability, ConnectwareError, ConnectwareErrorType, type CybusLog, CybusLogSource, Translation } from '../../../domain'

import type { ConnectwareLogsService, LogOptions, RawLogOptions, TranslationService } from '../../../application'

import type { BackendJsonResponseContent, BackendQueryParameters } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService } from '../Base'
import { LogsProcessor } from './Processor'

const PROTOCOL_MAPPER_AGENT = 'protocol-mapper'

type InternalOptions<M> = Readonly<{
    type: CybusLogSource
    container: LogOptions['resource']
    mapper: (logs: string) => readonly M[]
}> &
    Omit<LogOptions, 'resource' | 'levels'>

type BaseLogSourceConfiguration<T> = Readonly<{ fileName: Translation } & T>
type ExternallyLogSourceConfiguration = BaseLogSourceConfiguration<{ externalLoggerName: string, canLog: (id: string) => Promise<boolean> }>
type InternallyLogSourceConfiguration = BaseLogSourceConfiguration<{ isLoggingSource: true }>

type LogSourceConfiguration = ExternallyLogSourceConfiguration | InternallyLogSourceConfiguration

const isInternallyLogSourceConfiguration = (config: LogSourceConfiguration): config is InternallyLogSourceConfiguration => 'isLoggingSource' in config

export class ConnectwareHTTPLogsService extends FetchConnectwareHTTPService implements ConnectwareLogsService {
    private readonly supportsUntil = executeOnce(() =>
        this.request<'/api/core-containers/orchestrator', 'GET', boolean>({
            capability: Capability.LOGS_READ,
            method: 'GET',
            path: '/api/core-containers/orchestrator',
            authenticate: true,
            handlers: {
                200: (r) => r.getJson().then((value) => value.orchestrator === 'Docker'),
                503: () => new ConnectwareError(ConnectwareErrorType.SERVER_NOT_AVAILABLE, 'Unexpected network error while fetching containers metadata'),
            },
        })
    )

    private readonly logSourceConfiguration: ReadonlyRecord<CybusLogSource, LogSourceConfiguration> = {
        [CybusLogSource.SERVER]: this.createResourceLogConfiguration({ fileName: Translation.SERVER, externalLoggerName: PROTOCOL_MAPPER_AGENT }),
        [CybusLogSource.CONNECTION]: this.createResourceLogConfiguration({ fileName: Translation.CONNECTION, externalLoggerName: PROTOCOL_MAPPER_AGENT }),
        [CybusLogSource.SERVICE_CONTAINER]: { isLoggingSource: true, fileName: Translation.CONTAINER },
        [CybusLogSource.CORE_CONTAINER]: { isLoggingSource: true, fileName: Translation.CONTAINER },
        [CybusLogSource.ENDPOINT]: this.createResourceLogConfiguration({ fileName: Translation.ENDPOINT, externalLoggerName: PROTOCOL_MAPPER_AGENT }),
        [CybusLogSource.MAPPING]: this.createResourceLogConfiguration({ fileName: Translation.MAPPING, externalLoggerName: PROTOCOL_MAPPER_AGENT }),
        /** Services can always log, so have an easier implementation */
        [CybusLogSource.SERVICE]: { externalLoggerName: 'service-manager', canLog: () => Promise.resolve(true), fileName: Translation.SERVICE },
        [CybusLogSource.VOLUME]: this.createResourceLogConfiguration({ fileName: Translation.VOLUME, externalLoggerName: 'container-manager' }),
    }

    constructor (options: ConnectwareHTTPServiceOptions, private readonly translationService: TranslationService) {
        super(options)
    }

    private createResourceLogConfiguration (base: Pick<ExternallyLogSourceConfiguration, 'fileName' | 'externalLoggerName'>): ExternallyLogSourceConfiguration {
        return {
            ...base,
            canLog: (id) =>
                this.request<'/api/v2/resources/+', 'GET', boolean>({
                    capability: Capability.LOGS_READ,
                    path: '/api/v2/resources/+',
                    pathParams: [id],
                    method: 'GET',
                    authenticate: true,
                    handlers: {
                        /**
                         * Only Cybus resource instances within a certain list of agents can have logs
                         */
                        200: (r) => r.getJson().then((j) => j.data.runnerName === base.externalLoggerName),
                        404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Could not find resource to assert log availability', { id }),
                        500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'There was an issue while fetching log availability', { id }),
                    },
                }),
        }
    }

    private async fetchRelevantContainerNames (containerNameFilter: string): Promise<string[]> {
        const containers = await this.request<'/api/core-containers', 'GET', BackendJsonResponseContent<'/api/core-containers', 'GET', 200>>({
            capability: Capability.LOGS_READ,
            path: '/api/core-containers',
            method: 'GET',
            authenticate: true,
            handlers: { 200: (response) => response.getJson() },
        })

        /**
         * Filter relevant containers
         *
         * For each container
         *  Check if it is the `containerNameFilter`
         *  then add it to the list with some light transforming
         */
        return containers.flatMap(({ Names }) =>
            Names.flatMap((containerName) =>
                containerName && (containerName.includes(`_${containerNameFilter}`) || containerName.includes(`${containerNameFilter}-`))
                    ? [containerName.startsWith('/') ? containerName.slice(1) : containerName]
                    : []
            )
        )
    }

    private async fetchLoggingContainers (type: CybusLogSource, container: LogOptions['resource']): Promise<ReadonlyNonEmptyArray<string>> {
        const config = this.logSourceConfiguration[type]
        const externalLoggerName = 'externalLoggerName' in config ? config.externalLoggerName : null
        const filteredContainerNames = externalLoggerName ? await this.fetchRelevantContainerNames(externalLoggerName) : [container]

        assert(
            isArrayNotEmpty(filteredContainerNames) && !filteredContainerNames.includes(null),
            new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'No valid containers found', { type, container })
        )

        return filteredContainerNames as ReadonlyNonEmptyArray<string>
    }

    private async fetchContainerLogs<M> ({ type, container, lines, start, end, mapper }: InternalOptions<M>): Promise<readonly M[]> {
        const filteredContainerNames = await this.fetchLoggingContainers(type, container)

        const queryParams: Mutable<BackendQueryParameters<'/api/core-containers/+/logs'>> = {
            timestamps: 'true',
            tail: lines === 'all' ? lines : String(500),
        }

        if (start) {
            queryParams.since = String(start.getTime())
        }

        if (end && (await this.supportsUntil())) {
            queryParams.until = String(end.getTime())
        }

        return Promise.all(
            filteredContainerNames.map((loggerContainer) =>
                this.request<'/api/core-containers/+/logs', 'GET', readonly M[]>({
                    capability: Capability.LOGS_READ,
                    path: '/api/core-containers/+/logs',
                    pathParams: [loggerContainer],
                    method: 'GET',
                    authenticate: true,
                    queryParams,
                    handlers: {
                        200: (r) => r.getText().then(mapper),
                        404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Container not found', { loggerContainer }),
                    },
                })
            )
        ).then((allLines) => allLines.flat())
    }

    private getFileName (type: CybusLogSource, id: string | null): string {
        const config = this.logSourceConfiguration[type]

        /**
         * To figure out the file name
         * We either check if the resource itself does the logging
         * In which case, use it as the name of the file
         * Or use the general translation name
         */
        const name =
            isInternallyLogSourceConfiguration(config) && id
                ? `${this.getTimestamp()
                      .toISOString()
                      .split(/\D+/g)
                      .filter((v) => v)
                      .join('-')}-${id}`
                : this.translationService
                      .translate(Translation.ALL_RAW_LOGS, { type: this.translationService.translate(config.fileName, { count: 0 }) })
                      .toLowerCase()
                      .replaceAll(' ', '-')

        return `${name}.log`
    }

    // eslint-disable-next-line class-methods-use-this
    protected getTimestamp (): ReadonlyDate {
        return new Date()
    }

    doesCollectLogs (type: CybusLogSource, id: string): Promise<boolean> {
        const config = this.logSourceConfiguration[type]
        return Promise.resolve(isInternallyLogSourceConfiguration(config) || config.canLog(id))
    }

    async fetchLogs (type: CybusLogSource, { resource, levels, start, end, lines }: LogOptions): Promise<readonly CybusLog[]> {
        const processor = new LogsProcessor(
            /**
             * If the resource is a logging source itself, then a search should be done inside it
             * Therefore, no need to pass any parameters
             */
            isInternallyLogSourceConfiguration(this.logSourceConfiguration[type]) ? null : resource,
            levels,
            /** If setting the until is not supported on the BE, it needs to be done on the FE */
            end !== null && !(await this.supportsUntil()) ? end : null
        )

        return this.fetchContainerLogs({
            type,
            start,
            end,
            container: resource,
            lines,
            mapper: (logs) => processor.process(logs),
        })
    }

    async fetchAllRawLogs (type: CybusLogSource, { resource, lines }: RawLogOptions): Promise<File> {
        const logs = await this.fetchContainerLogs({
            type,
            container: resource,
            lines,
            start: null,
            end: null,
            mapper: (content) => [content],
        })

        return new File([logs.join('')], this.getFileName(type, resource), { type: 'data:text/plain;charset=utf-8' })
    }
}
